import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { templates, github, type TeamTemplate, type GitHubRepoGroup } from '../lib/api';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BOARD_COLOR_KEYS, getBoardColorHex } from '../lib/utils';

interface Props {
  onSubmit: (data: { name: string; description?: string; templateId: string; color?: string; githubRepo?: string; githubTokenId?: string }) => void;
  onClose: () => void;
}

export function CreateBoardModal({ onSubmit, onClose }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [color, setColor] = useState<string>(BOARD_COLOR_KEYS[0]);

  const { data: templateData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: templates.list,
  });

  const { data: ghStatusData } = useQuery({
    queryKey: ['github-status'],
    queryFn: github.status,
  });

  const ghConnected = ghStatusData?.data?.connected ?? false;

  const { data: reposData, isLoading: reposLoading } = useQuery({
    queryKey: ['github-repos'],
    queryFn: github.repos,
    enabled: ghConnected,
  });

  const templateList: TeamTemplate[] = templateData?.data || [];
  const repoGroups: GitHubRepoGroup[] = reposData?.data || [];

  useEscapeKey(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !templateId) return;
    let githubRepo: string | undefined;
    let githubTokenId: string | undefined;
    if (selectedRepo) {
      const colonIdx = selectedRepo.indexOf(':');
      if (colonIdx !== -1) {
        githubTokenId = selectedRepo.substring(0, colonIdx);
        githubRepo = selectedRepo.substring(colonIdx + 1);
      } else {
        githubRepo = selectedRepo;
      }
    }
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      templateId,
      color,
      githubRepo,
      githubTokenId,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-md rounded-xl border border-gray-800 bg-surface-850 p-6 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">Create Board</h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Board Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              placeholder="My Awesome Project"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              placeholder="What's this board for?"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Board Color
            </label>
            <div className="flex flex-wrap gap-2">
              {BOARD_COLOR_KEYS.map((key) => {
                const selected = color === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setColor(key)}
                    className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                      selected ? 'border-white' : 'border-transparent hover:border-gray-500'
                    }`}
                    style={{ background: getBoardColorHex(key) }}
                    title={key}
                    aria-label={`${key} color`}
                  />
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Cards in Command Center will show this color as a left border
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Team Template
            </label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
            >
              <option value="" disabled>
                {templatesLoading ? 'Loading templates...' : 'Select a template...'}
              </option>
              {templateList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Templates define workflow phases, agent roles, and auto-advance rules
            </p>
          </div>

          {ghConnected && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                GitHub Repository
              </label>
              <select
                value={selectedRepo}
                onChange={(e) => setSelectedRepo(e.target.value)}
                className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              >
                <option value="">
                  {reposLoading ? 'Loading repos...' : 'No repo (connect later)'}
                </option>
                {repoGroups.map((group) => (
                  <optgroup key={group.tokenId} label={`${group.label} (@${group.username})`}>
                    {group.error ? (
                      <option disabled>Error: {group.error}</option>
                    ) : (
                      group.repos.map((r) => (
                        <option key={`${group.tokenId}:${r.fullName}`} value={`${group.tokenId}:${r.fullName}`}>
                          {r.fullName}{r.private ? ' (private)' : ''}
                        </option>
                      ))
                    )}
                  </optgroup>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Link a repo for CI/CD tracking and agent workflows
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || !templateId}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
          >
            Create Board
          </button>
        </div>
      </form>
    </div>
  );
}
